import { useEffect, useState, useCallback } from "react";

// T-091 cadence (ADR-0007 D5 amended): count = lifetime SHOWS (max 5),
// dismissedAt = last "Not now" (no re-offer for 7 days), installed = the
// person accepted or `appinstalled` fired (never again), offered = once
// per browser session (sessionStorage, so a reload does not re-offer).
const INSTALL_COUNT_KEY = "pwa.install.count";
const DISMISSED_AT_KEY = "pwa.install.dismissedAt";
const INSTALLED_KEY = "pwa.install.installed";
const OFFERED_SESSION_KEY = "pwa.install.offered";
export const MAX_LIFETIME_SHOWS = 5;
export const REOFFER_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
}

let storedEvent: BeforeInstallPromptEvent | null = null;
const captureListeners = new Set<(event: BeforeInstallPromptEvent | null) => void>();

export function captureInstallPrompt(e: Event): void {
  if (!("prompt" in e)) return;
  e.preventDefault();
  storedEvent = e as BeforeInstallPromptEvent;
  for (const fn of captureListeners) fn(storedEvent);
}

export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return storedEvent;
}

/** Drop the captured event (after it was used, or between tests — the
 *  capture is module-level and would otherwise leak into a browser that
 *  never fires `beforeinstallprompt`). */
export function clearCapturedInstallPrompt(): void {
  storedEvent = null;
  for (const fn of captureListeners) fn(null);
}

export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as { MSStream?: unknown }).MSStream;
}

export function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua);
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const viaMedia =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  return viaMedia || (navigator as { standalone?: boolean }).standalone === true;
}

function getCount(): number {
  if (typeof localStorage === "undefined") return 0;
  const v = localStorage.getItem(INSTALL_COUNT_KEY);
  return v ? parseInt(v, 10) : 0;
}

function setCount(n: number): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(INSTALL_COUNT_KEY, String(n));
}

function getDismissedAt(): number {
  if (typeof localStorage === "undefined") return 0;
  const v = localStorage.getItem(DISMISSED_AT_KEY);
  return v ? parseInt(v, 10) : 0;
}

function setDismissedAt(now: number): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(DISMISSED_AT_KEY, String(now));
}

function isInstalledFlag(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(INSTALLED_KEY) === "1";
}

export function shouldShowPrompt(): boolean {
  if (isInstalledFlag()) return false;
  const count = getCount();
  if (Number.isNaN(count) || count >= MAX_LIFETIME_SHOWS) return false;
  const dismissed = getDismissedAt();
  if (dismissed > 0 && Date.now() - dismissed < REOFFER_AFTER_MS) return false;
  return true;
}

/** One lifetime show consumed. Called when the card is first rendered. */
export function recordShown(): void {
  setCount(getCount() + 1);
}

/** "Not now" (or the native prompt dismissed): pause for 7 days. */
export function recordDismiss(): void {
  setDismissedAt(Date.now());
}

/** Accepted, or `appinstalled` fired: never offer again. (The previous
 *  implementation stored `Infinity`, which parses back to NaN and never
 *  blocked anything.) */
export function recordInstalled(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(INSTALLED_KEY, "1");
}

export function offeredThisSession(): boolean {
  try {
    return typeof sessionStorage !== "undefined" && sessionStorage.getItem(OFFERED_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function markOfferedThisSession(): void {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(OFFERED_SESSION_KEY, "1");
  } catch {
    /* private mode / storage disabled: the in-memory `offered` state still gates this mount */
  }
}

export function useInstallPrompt(): {
  event: BeforeInstallPromptEvent | null;
  /** Shows the native prompt; resolves to the outcome, or null when there is no event. */
  prompt: () => Promise<"accepted" | "dismissed" | null>;
  isIOS: boolean;
  show: boolean;
  dismiss: () => void;
  installed: boolean;
} {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(storedEvent);
  const [installed, setInstalled] = useState(isInstalledFlag);

  useEffect(() => {
    const listener = (e: Event) => {
      captureInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", listener);
    const onAppInstalled = () => {
      setInstalled(true);
      recordInstalled();
    };
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", listener);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  useEffect(() => {
    const sub = (e: BeforeInstallPromptEvent | null) => setEvent(e);
    captureListeners.add(sub);
    if (storedEvent) setEvent(storedEvent);
    return () => {
      captureListeners.delete(sub);
    };
  }, []);

  const prompt = useCallback(async (): Promise<"accepted" | "dismissed" | null> => {
    if (!event) return null;
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === "accepted") {
      recordInstalled();
      setInstalled(true);
    } else {
      recordDismiss();
    }
    setEvent(null);
    return choice.outcome;
  }, [event]);

  const dismiss = useCallback(() => {
    recordDismiss();
    setEvent(null);
  }, []);

  return {
    event,
    prompt,
    isIOS: isIos(),
    show: !installed,
    dismiss,
    installed,
  };
}
